/**
 * Single source of truth for serializing Employee Profile Settings.
 *
 * Mirrors `serializeCardSettings.js`, `serializeLocationSettings.js`, and
 * `serializeOrganizationSettings.js`. See those doc-blocks for rationale.
 * Same INVARIANT applies:
 *
 *   Both the snapshot (built from the API employee object) AND the live
 *   form state MUST be produced by THIS exact same function. Adding a new
 *   field = ONE row in FIELDS — both sides automatically stay in sync.
 *
 * Replaces parallel raw-object comparison logic in `Employees.jsx`.
 */

// ── Coercion helpers ──

const _toStr = (v, d = '') => (v === null || v === undefined ? d : String(v).trim());

// ── Field registry — ONE place ──
//
// Each entry: { key, employee, state, type, default }
const FIELDS = [
    { key: 'name',       employee: 'name',         state: 'name',         type: 'str', default: '' },
    { key: 'email',      employee: 'email',        state: 'email',        type: 'str', default: '' },
    { key: 'phone',      employee: 'phone',        state: 'phone',        type: 'str', default: '' },
    { key: 'locationId', employee: 'location_id',  state: 'location_id',  type: 'str', default: '' },
];

function normalize(v, type, def) {
    switch (type) {
        case 'str': return _toStr(v, def);
        default:    return v ?? def;
    }
}

/**
 * Serialize either an employee (from the API) or a flat form-state
 * object to a stable JSON string.
 *
 * @param {Object} input                   Employee object OR formData
 * @param {'employee'|'state'} source      Tells the function which key style to read.
 * @returns {string} JSON string suitable for === comparison.
 */
export function serializeEmployeeSettings(input, source = 'employee') {
    if (!input || typeof input !== 'object') return JSON.stringify({});

    // P_PORTAL_SAVE_INTEGRITY_AUDIT — dev-time coverage guard.
    if (
        source === 'state'
        && typeof process !== 'undefined'
        && process.env
        && process.env.NODE_ENV !== 'production'
    ) {
        if (!serializeEmployeeSettings._warned) serializeEmployeeSettings._warned = new Set();
        for (const f of FIELDS) {
            if (!Object.prototype.hasOwnProperty.call(input, f.state)
                && !serializeEmployeeSettings._warned.has(f.state)) {
                serializeEmployeeSettings._warned.add(f.state);
                // eslint-disable-next-line no-console
                console.error(
                    `[SAVE_INTEGRITY] serializeEmployeeSettings(state) missing key: "${f.state}". ` +
                    'Save Changes will never enable for this field.'
                );
            }
        }
    }

    const out = {};
    for (const f of FIELDS) {
        const raw = source === 'employee' ? input[f.employee] : input[f.state];
        out[f.key] = normalize(raw, f.type, f.default);
    }
    return JSON.stringify(out);
}

/** Convenience export — list of canonical keys. */
export const EMPLOYEE_SETTING_KEYS = FIELDS.map(f => f.key);

/** State-bag key names (snake_case, as written into React form state). */
export const EMPLOYEE_STATE_KEYS = FIELDS.map(f => f.state);
